import { LinkIcon } from "lucide-react";
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Spinner,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@capacit-com/lara-law-shared-ui";
import { FC, useState } from "react";
import useDocumentLink from "@/hooks/useDocumentLink";
import { showErrorToast, showSuccessToast } from "../custom-toasts/toasts";
import { logger } from "@/lib/logger";
import { buildDocumentLinkUrl } from "@/utils/domain";
// Static/non-static only meaningful for Retsinformation (LBK supersession).
// See CreateDocumentLinkTemplateRequestModel.IsDestinationStatic.
import { DataSource, normalizeDataSource } from "@/config/datasources";
import { ReferenceTypePicker } from "@/components/document-link/ReferenceTypePicker";

type DocumentLinkButtonProps = {
  isHovering: boolean;
  documentId: string;
  source: string;
  className?: string;
  strokeWidth?: number;
  container?: HTMLElement;
};

const DocumentLinkButton: FC<DocumentLinkButtonProps> = ({
  isHovering,
  documentId,
  source,
  className,
  strokeWidth = 2,
  container,
}) => {
  const { postTemplate } = useDocumentLink();
  const isLoading = postTemplate.isPending;
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [referenceType, setReferenceType] = useState<"static" | "nonstatic">("nonstatic");

  const isRetsinformationLegislation =
    normalizeDataSource(source) === DataSource.Retsinformation;

  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isRetsinformationLegislation) {
      setReferenceType("nonstatic");
      setIsPopoverOpen(true);
    } else {
      await createReference(true);
    }
  };

  const createReference = async (isStatic: boolean) => {
    try {
      const refId = await postTemplate.mutateAsync({
        destinationDocumentId: documentId,
        destinationSource: source,
        destinationHtmlIds: [],
        isDestinationStatic: isStatic,
      });

      if (refId) {
        const documentLinkUrl = buildDocumentLinkUrl(refId);
        navigator.clipboard.writeText(documentLinkUrl);
        showSuccessToast("Dokumentlink kopieret til udklipsholderen");
      } else {
        logger.error("Document link template response has no id", {
          operation: "createDocumentLinkTemplate",
          documentId,
          source,
          isStatic,
        });
        showErrorToast("Fejl ved hentning af dokumentlink");
      }
    } catch (error) {
      showErrorToast(
        "Der opstod en fejl ved oprettelse af dokumentlink: " +
          (error as Error).message
      );
    } finally {
      setIsPopoverOpen(false);
    }
  };

  const handleConfirm = () => {
    createReference(referenceType === "static");
  };

  const triggerButton = (
    <Button
      variant="ghost"
      size="icon"
      className={className + " p-2"}
      onClick={handleClick}
      disabled={isLoading}
    >
      <LinkIcon className="size-4" strokeWidth={strokeWidth} />
    </Button>
  );

  return (
    <>
      {(isHovering || isPopoverOpen) && !isLoading && (
        isRetsinformationLegislation ? (
          <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
            <Tooltip>
              <TooltipTrigger asChild>
                <PopoverTrigger asChild>
                  {triggerButton}
                </PopoverTrigger>
              </TooltipTrigger>
              <TooltipContent container={container}>
                Kopier dokumentlink
              </TooltipContent>
            </Tooltip>
            <PopoverContent
              align="end"
              className="w-auto p-3"
              onOpenAutoFocus={(e) => e.preventDefault()}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <ReferenceTypePicker
                value={referenceType}
                onChange={setReferenceType}
                onConfirm={handleConfirm}
                isPending={isLoading}
                idPrefix="doc-link-ref-type"
              />
            </PopoverContent>
          </Popover>
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              {triggerButton}
            </TooltipTrigger>
            <TooltipContent container={container}>
              Kopier dokumentlink
            </TooltipContent>
          </Tooltip>
        )
      )}
      {isLoading && (
        <Button
          variant="ghost"
          size="icon"
          className={className + " p-2"}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <Spinner className="size-4" />
        </Button>
      )}
    </>
  );
};

export default DocumentLinkButton;
